import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '../../utils/debounce';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Card,
    CardContent,
    Alert,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Grid,
    Snackbar,
    tooltipClasses,
    TooltipProps,
    Autocomplete,
    CircularProgress,
    LinearProgress,
} from '@mui/material';
import { Checkbox } from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Search,
    Leaderboard,
    LocationCity,
    HowToVote,
    RestartAlt,
    Close,
    FileDownload,
    GroupAdd,
    People,
    CheckCircle,
    Error as ErrorIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import api from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';
import VillageAutocomplete from '../../components/common/VillageAutocomplete';

// --- Interfaces ---
import { SubLeader } from '../../types/sub_leader';
import { Constituency } from '../../types/constituency';
import { Village } from '../../types/village';
import { VotingBooth } from '../../types/voting_booth';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

// Interfaces removed in favor of generic types

interface SubLeaderFormData {
    first_name: string;
    last_name: string;
    phone: string;
    whatsapp_number: string;
    gender: 'male' | 'female' | 'other';
    age: string;
    community: string;
    voter_id: string;
    adhaar_number: string;
    position: string;
    constituency_id: string; // use string for Select value handling (empty)
    village_id: string;
    voting_booth_id: string;
}

interface LocationOption {
    id: number;
    name: string;
}

const SubLeaders = () => {
    const queryClient = useQueryClient();
    const { hasPermission } = useAuth();
    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [open, setOpen] = useState(false);
    const [selectedSubLeader, setSelectedSubLeader] = useState<SubLeader | null>(null);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [subLeaderToDelete, setSubLeaderToDelete] = useState<SubLeader | null>(null);

    // Bulk Assign State
    const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
    const [bulkAssignSubLeader, setBulkAssignSubLeader] = useState<SubLeader | null>(null);
    const [baConstituencies, setBaConstituencies] = useState<LocationOption[]>([]);
    const [baVillages, setBaVillages] = useState<LocationOption[]>([]);
    const [baBooths, setBaBooths] = useState<LocationOption[]>([]);
    const [baGender, setBaGender] = useState('');
    const [baMinAge, setBaMinAge] = useState('');
    const [baMaxAge, setBaMaxAge] = useState('');
    const [baCommunity, setBaCommunity] = useState('');
    const [baHasSubLeader, setBaHasSubLeader] = useState('');
    const [baSearch, setBaSearch] = useState('');
    const debouncedBaSearch = useDebounce(baSearch, 300);
    const [baPageModel, setBaPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
    const [selectedCitizenIds, setSelectedCitizenIds] = useState<number[]>([]);
    const [selectAllFiltered, setSelectAllFiltered] = useState(false);

    // Form State
    const [formData, setFormData] = useState<SubLeaderFormData>({
        first_name: '',
        last_name: '',
        phone: '',
        whatsapp_number: '',
        gender: 'male',
        age: '',
        community: '',
        voter_id: '',
        adhaar_number: '',
        position: '',
        constituency_id: '',
        village_id: '',
        voting_booth_id: '',
    });

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // --- Queries ---
    const { data: subLeadersData, isLoading: subLeadersLoading } = useQuery({
        queryKey: ['sub-leaders', pageModel.page, pageModel.pageSize, debouncedSearch],
        queryFn: async () => {
            const params: any = { page: pageModel.page + 1, per_page: pageModel.pageSize };
            if (debouncedSearch) params.search = debouncedSearch;

            const res = await api.get('/tenant/sub-leaders', { params });
            return res.data;
        },
        placeholderData: keepPreviousData,
    });

    // Reset page when search changes
    React.useEffect(() => {
        setPageModel(prev => ({ ...prev, page: 0 }));
    }, [debouncedSearch]);

    // Dependencies for Filters
    const { data: constituenciesData = [], isLoading: constituenciesLoading } = useQuery({
        queryKey: ['constituencies-filter'],
        queryFn: async () => {
            const res = await api.get('/tenant/constituencies', { params: { per_page: 100 } });
            return res.data.data.data || res.data.data || [];
        }
    });

    // Dependencies for Form (Dropdowns need all options typically, or refined search)
    // We can reuse the filter data if appropriate, or keep separate specific queries for form dependencies
    // For simplicity, let's keep form dependencies separate to avoid confusion with filter state
    const { data: formConstituencies = [] } = useQuery({
        queryKey: ['constituencies-form'],
        queryFn: async () => {
            const res = await api.get('/tenant/constituencies', { params: { per_page: 1000 } });
            return res.data.data.data;
        },
        enabled: open // Only fetch when form is open
    });

    const formActiveConstituencyId = formData.constituency_id;
    const { data: formVillages = [] } = useQuery({
        queryKey: ['villages-form', formActiveConstituencyId],
        enabled: !!formActiveConstituencyId && open,
        queryFn: async () => {
            const res = await api.get('/tenant/villages', {
                params: {
                    constituency_id: formActiveConstituencyId,
                    per_page: 1000
                }
            });
            return res.data.data.data;
        }
    });

    const formActiveVillageId = formData.village_id;
    const { data: formVotingBooths = [] } = useQuery({
        queryKey: ['voting-booths-form', formActiveVillageId],
        enabled: !!formActiveVillageId && open,
        queryFn: async () => {
            const res = await api.get('/tenant/voting-booths', {
                params: {
                    village_id: formActiveVillageId,
                    per_page: 1000
                }
            });
            return res.data.data.data;
        }
    });

    // Fetch Communities Options
    const { data: communitiesOptions } = useQuery({
        queryKey: ['communities-options'],
        queryFn: async () => {
            const res = await api.get('/tenant/options/communities');
            return res.data?.data || [];
        }
    });

    // Bulk Assign: Build filter params helper
    const buildBulkAssignFilterParams = useCallback(() => {
        const params: any = {};
        if (baConstituencies.length > 0) params['constituency_id[]'] = baConstituencies.map(c => c.id);
        if (baVillages.length > 0) params['village_id[]'] = baVillages.map(v => v.id);
        if (baBooths.length > 0) params['voting_booth_id[]'] = baBooths.map(b => b.id);
        if (baGender) params.gender = baGender;
        if (baMinAge) params.min_age = parseInt(baMinAge, 10);
        if (baMaxAge) params.max_age = parseInt(baMaxAge, 10);
        if (baCommunity) params.community = baCommunity;
        if (baHasSubLeader === 'unassigned') params.sub_leader_id = 'null';
        if (baHasSubLeader === 'assigned') params.has_sub_leader = '1';
        return params;
    }, [baConstituencies, baVillages, baBooths, baGender, baMinAge, baMaxAge, baCommunity, baHasSubLeader]);

    // Bulk Assign: Citizen list query (paginated)
    const { data: baCitizensData, isLoading: baCitizensLoading } = useQuery({
        queryKey: ['ba-citizens', baPageModel.page, baPageModel.pageSize, debouncedBaSearch,
            baConstituencies.map(c => c.id), baVillages.map(v => v.id), baBooths.map(b => b.id),
            baGender, baMinAge, baMaxAge, baCommunity, baHasSubLeader],
        queryFn: async () => {
            const params: any = {
                page: baPageModel.page + 1,
                per_page: baPageModel.pageSize,
                ...buildBulkAssignFilterParams(),
            };
            if (debouncedBaSearch) params.search = debouncedBaSearch;
            const res = await api.get('/tenant/citizens', { params });
            return res.data;
        },
        enabled: bulkAssignOpen,
        placeholderData: keepPreviousData,
    });

    // Reset citizen page when filters change
    useEffect(() => {
        setBaPageModel(prev => ({ ...prev, page: 0 }));
    }, [baConstituencies, baVillages, baBooths, baGender, baMinAge, baMaxAge, baCommunity, baHasSubLeader, debouncedBaSearch]);

    // Bulk Assign: Booths for dialog (based on selected ba villages)
    const { data: baBoothsData = [], isLoading: baBoothsLoading } = useQuery({
        queryKey: ['ba-booths-filter', baVillages.map(v => v.id)],
        queryFn: async () => {
            const params: any = { per_page: 500 };
            if (baVillages.length > 0) {
                params['village_id[]'] = baVillages.map(v => v.id);
            }
            const res = await api.get('/tenant/voting-booths', { params });
            return res.data.data.data || res.data.data || [];
        },
        enabled: bulkAssignOpen,
    });

    // Bulk Assign: Progress polling
    const [pollingSubLeaderId, setPollingSubLeaderId] = useState<number | null>(null);

    const { data: bulkAssignStatusData } = useQuery({
        queryKey: ['bulk-assign-status', pollingSubLeaderId],
        queryFn: async () => {
            const res = await api.get(`/tenant/sub-leaders/${pollingSubLeaderId}/bulk-assign-status`);
            return res.data?.data;
        },
        enabled: !!pollingSubLeaderId,
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            if (status === 'pending' || status === 'processing') return 2000;
            return false;
        },
    });

    // Stop polling when assignment completes
    useEffect(() => {
        if (bulkAssignStatusData && (bulkAssignStatusData.status === 'completed' || bulkAssignStatusData.status === 'failed')) {
            if (bulkAssignStatusData.status === 'completed') {
                showSnackbar(`Bulk assignment completed! ${bulkAssignStatusData.assigned_citizens || 0} citizens assigned.`, 'success');
                queryClient.invalidateQueries({ queryKey: ['sub-leaders'] });
            } else {
                showSnackbar(`Bulk assignment failed: ${bulkAssignStatusData.error || 'Unknown error'}`, 'error');
            }
            setTimeout(() => setPollingSubLeaderId(null), 3000);
        }
    }, [bulkAssignStatusData]);

    // Bulk Assign: Mutation - supports both specific IDs and filter-based (select all)
    const bulkAssignMutation = useMutation({
        mutationFn: async ({ subLeaderId, citizenIds, selectAll, filters }: {
            subLeaderId: number;
            citizenIds?: number[];
            selectAll?: boolean;
            filters?: any;
        }) => {
            if (selectAll) {
                // Filter-based bulk assign via queue job
                const params: any = {};
                if (filters.baConstituencies?.length > 0) params.constituency_ids = filters.baConstituencies.map((c: LocationOption) => c.id);
                if (filters.baVillages?.length > 0) params.village_ids = filters.baVillages.map((v: LocationOption) => v.id);
                if (filters.baBooths?.length > 0) params.voting_booth_ids = filters.baBooths.map((b: LocationOption) => b.id);
                if (filters.baGender) params.gender = filters.baGender;
                if (filters.baMinAge) params.min_age = parseInt(filters.baMinAge, 10);
                if (filters.baMaxAge) params.max_age = parseInt(filters.baMaxAge, 10);
                if (filters.baCommunity) params.community = filters.baCommunity;
                if (filters.baHasSubLeader) params.has_sub_leader = filters.baHasSubLeader;
                return api.post(`/tenant/sub-leaders/${subLeaderId}/bulk-assign`, params);
            } else {
                // Direct citizen IDs assignment
                return api.post('/tenant/citizens/assign-sub-leader', {
                    citizen_ids: citizenIds,
                    sub_leader_id: subLeaderId,
                });
            }
        },
        onSuccess: (res, variables) => {
            setBulkAssignOpen(false);
            if (variables.selectAll) {
                setPollingSubLeaderId(variables.subLeaderId);
                showSnackbar('Bulk assignment started! Tracking progress...', 'success');
            } else {
                showSnackbar(res.data?.message || `${variables.citizenIds?.length} citizen(s) assigned successfully`, 'success');
                queryClient.invalidateQueries({ queryKey: ['sub-leaders'] });
            }
        },
        onError: (err: any) => {
            if (err.response?.status === 409) {
                showSnackbar('A bulk assignment is already in progress for this sub leader', 'error');
            } else {
                showSnackbar(err.response?.data?.message || 'Failed to assign citizens', 'error');
            }
        }
    });
    const handleMutationError = (err: any) => {
        if (err.response?.status === 422 && err.response?.data?.errors) {
            const newErrors: Record<string, string> = {};

            Object.entries(err.response.data.errors).forEach(([key, msgs]: [string, any]) => {
                const msg = Array.isArray(msgs) ? msgs[0] : msgs;
                if (['first_name', 'last_name', 'phone', 'whatsapp_number', 'gender', 'age', 'community', 'voter_id', 'adhaar_number', 'position', 'constituency_id', 'village_id', 'voting_booth_id'].includes(key)) {
                    newErrors[key] = msg;
                } else {
                    showSnackbar(msg, 'error');
                }
            });
            setFormErrors(newErrors);
        } else {
            showSnackbar(err.response?.data?.message || 'An error occurred', 'error');
        }
    };

    const saveMutation = useMutation({
        mutationFn: async (data: SubLeaderFormData) => {
            const payload: any = { ...data };
            // Convert empty strings to null for optional fields so server knows to clear them
            payload.voting_booth_id = payload.voting_booth_id || null;
            payload.age = payload.age || null;
            payload.community = payload.community || null;
            payload.voter_id = payload.voter_id || null;
            payload.adhaar_number = payload.adhaar_number || null;
            payload.whatsapp_number = payload.whatsapp_number || null;

            if (selectedSubLeader) {
                return api.put(`/tenant/sub-leaders/${selectedSubLeader.id}`, payload);
            } else {
                return api.post('/tenant/sub-leaders', payload);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sub-leaders'] });
            handleClose();
            showSnackbar(`Sub leader ${selectedSubLeader ? 'updated' : 'added'} successfully`, 'success');
        },
        onError: handleMutationError
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            return api.delete(`/tenant/sub-leaders/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sub-leaders'] });
            showSnackbar('Sub leader deleted successfully', 'success');
        },
        onError: (err: any) => showSnackbar(err.response?.data?.message || 'Failed to delete sub leader', 'error')
    });

    // --- Handlers ---
    const handleOpen = (subLeader?: SubLeader) => {
        if (subLeader) {
            setSelectedSubLeader(subLeader);
            setFormData({
                first_name: subLeader.first_name,
                last_name: subLeader.last_name,
                phone: subLeader.phone,
                whatsapp_number: subLeader.whatsapp_number || '',
                gender: subLeader.gender,
                age: subLeader.age?.toString() || '',
                community: (subLeader as any).community || '',
                voter_id: (subLeader as any).voter_id || '',
                adhaar_number: (subLeader as any).adhaar_number || '',
                position: (subLeader as any).position || '',
                constituency_id: subLeader.constituency_id.toString(),
                village_id: subLeader.village_id.toString(),
                voting_booth_id: subLeader.voting_booth_id?.toString() || '',
            });
        } else {
            setSelectedSubLeader(null);
            setFormData({
                first_name: '',
                last_name: '',
                phone: '',
                whatsapp_number: '',
                gender: 'male',
                age: '',
                community: '',
                voter_id: '',
                adhaar_number: '',
                position: '',
                constituency_id: '',
                village_id: '',
                voting_booth_id: '',
            });
        }
        setFormErrors({});
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedSubLeader(null);
        setFormErrors({});
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        saveMutation.mutate(formData);
    };

    const handleDeleteClick = (subLeader: SubLeader) => {
        setSubLeaderToDelete(subLeader);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (subLeaderToDelete) {
            deleteMutation.mutate(subLeaderToDelete.id);
        }
        setDeleteDialogOpen(false);
        setSubLeaderToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSubLeaderToDelete(null);
    };

    // Bulk Assign Handlers
    const handleBulkAssignOpen = (subLeader: SubLeader) => {
        setBulkAssignSubLeader(subLeader);
        setBaConstituencies([]);
        setBaVillages([]);
        setBaBooths([]);
        setBaGender('');
        setBaMinAge('');
        setBaMaxAge('');
        setBaCommunity('');
        setBaHasSubLeader('');
        setBaSearch('');
        setBaPageModel({ page: 0, pageSize: 25 });
        setSelectedCitizenIds([]);
        setSelectAllFiltered(false);
        setBulkAssignOpen(true);
    };

    const handleBulkAssignClose = () => {
        setBulkAssignOpen(false);
        setBulkAssignSubLeader(null);
    };

    const handleBulkAssignConfirm = () => {
        if (!bulkAssignSubLeader) return;
        if (selectAllFiltered) {
            // Use queue job for all matching filters
            bulkAssignMutation.mutate({
                subLeaderId: bulkAssignSubLeader.id,
                selectAll: true,
                filters: { baConstituencies, baVillages, baBooths, baGender, baMinAge, baMaxAge, baCommunity, baHasSubLeader },
            });
        } else {
            // Use direct assignment for selected IDs
            bulkAssignMutation.mutate({
                subLeaderId: bulkAssignSubLeader.id,
                citizenIds: selectedCitizenIds,
            });
        }
    };

    const totalFilteredCitizens = baCitizensData?.data?.total || 0;
    const assignCount = selectAllFiltered ? totalFilteredCitizens : selectedCitizenIds.length;

    // Citizen DataGrid columns for the dialog
    const citizenColumns: GridColDef[] = [
        { field: 'name', headerName: 'Name', minWidth: 180, flex: 1 },
        { field: 'voter_id', headerName: 'Voter ID', width: 120, renderCell: (p) => p.value || '-' },
        { field: 'phone', headerName: 'Phone', width: 120, renderCell: (p) => p.value || '-' },
        { field: 'gender', headerName: 'Gender', width: 80, renderCell: (p) => p.value ? p.value.charAt(0).toUpperCase() + p.value.slice(1) : '-' },
        { field: 'age', headerName: 'Age', width: 60, type: 'number' },
        { field: 'constituency', headerName: 'Constituency', width: 130, valueGetter: (v, row) => row.constituency?.name || '-' },
        { field: 'village', headerName: 'Village', width: 130, valueGetter: (v, row) => row.village?.name || '-' },
        {
            field: 'sub_leader', headerName: 'Current Sub Leader', width: 150,
            renderCell: (p) => p.row.sub_leader ? (
                <Chip label={`${p.row.sub_leader.first_name} ${p.row.sub_leader.last_name}`} size="small" color="warning" variant="outlined" />
            ) : <Chip label="Unassigned" size="small" color="default" variant="outlined" />
        },
    ];

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Sr. No',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        {
            field: 'full_name',
            headerName: 'Name',
            minWidth: 180,
            flex: 1,
            valueGetter: (v, row) => `${row.first_name} ${row.last_name}`
        },
        {
            field: 'position',
            headerName: 'Position',
            width: 150,
            renderCell: (params) => params.value ? (
                <Chip label={params.value} size="small" color="primary" variant="outlined" />
            ) : '-'
        },
        {
            field: 'phone',
            headerName: 'Phone',
            width: 130,
        },
        {
            field: 'whatsapp_number',
            headerName: 'WhatsApp',
            width: 130,
            renderCell: (params) => params.value || '-'
        },
        {
            field: 'voter_id',
            headerName: 'Voter ID',
            width: 120,
            renderCell: (params) => params.value || '-'
        },
        { field: 'constituency', headerName: 'Constituency', width: 150, valueGetter: (v, row) => row.constituency?.name || 'N/A' },
        { field: 'village', headerName: 'Village', width: 150, valueGetter: (v, row) => row.village?.name || 'N/A' },
        { field: 'citizens_count', headerName: 'Voters Managed', width: 120, type: 'number' },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 180,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    {hasPermission('sub-leaders.update') && (
                        <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: '#1976d2' }}>
                            <BootstrapTooltip title="Bulk Assign Citizens" placement="top">
                                <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleBulkAssignOpen(params.row)}>
                                    <GroupAdd fontSize="small" />
                                </IconButton>
                            </BootstrapTooltip>
                        </Button>
                    )}
                    {hasPermission('sub-leaders.update') && (
                        <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'green' }}>
                            <BootstrapTooltip title="Edit" placement="top">
                                <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleOpen(params.row)}>
                                    <Edit fontSize="small" />
                                </IconButton>
                            </BootstrapTooltip>
                        </Button>
                    )}
                    {hasPermission('sub-leaders.delete') && (
                        <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'red' }}>
                            <BootstrapTooltip title="Delete" placement="top">
                                <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => handleDeleteClick(params.row)}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </BootstrapTooltip>
                        </Button>
                    )}
                </Box>
            ),
        },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Leaderboard color="primary" />
                    <Typography variant="h4" sx={{ fontSize: { xs: '1.25rem', sm: '1.6rem' } }}>Sub Leaders Management</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {hasPermission('sub-leaders.create') && (
                        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                            Add Sub Leader
                        </Button>
                    )}
                </Box>
            </Box>

            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ py: 2 }}>
                    <Grid container spacing={2} display="flex" justifyContent="space-between" alignItems="center">

                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search sub leaders..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                                }}
                            />
                        </Grid>
                        {/* Filters can be expanded here */}

                        <Button
                            variant="outlined"
                            startIcon={<FileDownload />}
                            onClick={async () => {
                                try {
                                    const response = await api.get('/tenant/sub-leaders/export', {
                                        responseType: 'blob'
                                    });
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute('download', 'sub-leaders.xlsx');
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    window.URL.revokeObjectURL(url);
                                    showSnackbar('Export downloaded successfully', 'success');
                                } catch (err: any) {
                                    showSnackbar(err.response?.data?.message || 'Failed to export', 'error');
                                }
                            }}
                        >
                            Export
                        </Button>
                    </Grid>
                </CardContent>
            </Card>

            <Card>
                <DataGrid
                    rows={subLeadersData?.data?.data || subLeadersData?.data || []}
                    columns={columns}
                    loading={subLeadersLoading}
                    paginationModel={pageModel}
                    onPaginationModelChange={setPageModel}
                    pageSizeOptions={[10, 25, 50]}
                    paginationMode="server"
                    rowCount={subLeadersData?.data?.total || subLeadersData?.total || 0}
                    autoHeight
                    disableRowSelectionOnClick
                    sx={{ border: 0 }}
                />
            </Card>

            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={window.innerWidth < 600}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 22 }}>
                    {selectedSubLeader ? 'Edit Sub Leader' : 'Add Sub Leader'}
                    <IconButton onClick={handleClose} size="small">
                        <Close />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>Personal Information</Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="First Name"
                                    fullWidth
                                    required
                                    value={formData.first_name}
                                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                    error={!!formErrors.first_name}
                                    helperText={formErrors.first_name}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Last Name"
                                    fullWidth
                                    required
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                    error={!!formErrors.last_name}
                                    helperText={formErrors.last_name}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Phone"
                                    fullWidth
                                    required
                                    value={formData.phone}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                        setFormData({ ...formData, phone: value });
                                    }}
                                    error={!!formErrors.phone}
                                    helperText={formErrors.phone}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="WhatsApp Number"
                                    fullWidth
                                    value={formData.whatsapp_number}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                        setFormData({ ...formData, whatsapp_number: value });
                                    }}
                                    error={!!formErrors.whatsapp_number}
                                    helperText={formErrors.whatsapp_number}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Voter ID"
                                    fullWidth
                                    value={formData.voter_id}
                                    onChange={(e) => setFormData({ ...formData, voter_id: e.target.value })}
                                    error={!!formErrors.voter_id}
                                    helperText={formErrors.voter_id}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Aadhaar Number"
                                    fullWidth
                                    value={formData.adhaar_number}
                                    onChange={(e) => {
                                        setFormData({ ...formData, adhaar_number: e.target.value });
                                    }}
                                    error={!!formErrors.adhaar_number}
                                    helperText={formErrors.adhaar_number}
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 12 }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <FormControl fullWidth required>
                                    <InputLabel>Gender</InputLabel>
                                    <Select
                                        value={formData.gender}
                                        label="Gender"
                                        onChange={(e) => setFormData({ ...formData, gender: e.target.value as any })}
                                    >
                                        <MenuItem value="male">Male</MenuItem>
                                        <MenuItem value="female">Female</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <TextField
                                    label="Age"
                                    type="number"
                                    fullWidth
                                    value={formData.age}
                                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                                    error={!!formErrors.age}
                                    helperText={formErrors.age}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Autocomplete
                                    freeSolo
                                    options={communitiesOptions?.map((c: any) => c.name) || []}
                                    value={formData.community}
                                    onChange={(event, newValue) => {
                                        setFormData({ ...formData, community: newValue || '' });
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        setFormData({ ...formData, community: newInputValue });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Community"
                                            fullWidth
                                            variant="outlined"
                                            error={!!formErrors.community}
                                            helperText={formErrors.community}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>

                        <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 2 }}>Position / Designation</Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    label="Position"
                                    fullWidth
                                    value={formData.position}
                                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                                    error={!!formErrors.position}
                                    helperText={formErrors.position || 'e.g. Mandal President, Booth Incharge, Youth Leader'}
                                    placeholder="Enter position or designation"
                                />
                            </Grid>
                        </Grid>

                        <Typography variant="subtitle2" color="primary" sx={{ mt: 3, mb: 2 }}>Jurisdiction</Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <FormControl fullWidth required>
                                    <InputLabel>Constituency</InputLabel>
                                    <Select
                                        value={formData.constituency_id}
                                        label="Constituency"
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            constituency_id: e.target.value,
                                            village_id: '', // Reset lower params
                                            voting_booth_id: ''
                                        })}
                                        error={!!formErrors.constituency_id}
                                    >
                                        {formConstituencies.map((c: any) => (
                                            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                        ))}
                                    </Select>
                                    {formConstituencies.length === 0 && <Alert severity="warning" sx={{ mt: 1 }}>No Constituencies found.</Alert>}
                                    {formErrors.constituency_id && <Typography variant="caption" color="error">{formErrors.constituency_id}</Typography>}
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <FormControl fullWidth required>
                                    <InputLabel>Village</InputLabel>
                                    <Select
                                        value={formData.village_id}
                                        label="Village"
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            village_id: e.target.value,
                                            voting_booth_id: ''
                                        })}
                                        disabled={!formData.constituency_id}
                                        error={!!formErrors.village_id}
                                    >
                                        {formVillages.map((v: any) => (
                                            <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                                        ))}
                                    </Select>
                                    {formErrors.village_id && <Typography variant="caption" color="error">{formErrors.village_id}</Typography>}
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Voting Booth</InputLabel>
                                    <Select
                                        value={formData.voting_booth_id}
                                        label="Voting Booth"
                                        onChange={(e) => setFormData({ ...formData, voting_booth_id: e.target.value })}
                                        disabled={!formData.village_id}
                                        error={!!formErrors.voting_booth_id}
                                    >
                                        {formVotingBooths.map((vb: any) => (
                                            <MenuItem key={vb.id} value={vb.id}>{vb.name}{vb.number ? ` (${vb.number})` : ''}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                        <Button type="submit" color="success" variant="contained" disabled={saveMutation.isPending}>
                            {saveMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
                <Box sx={{ bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #fecaca' }}>
                    <Box sx={{ bgcolor: '#dc2626', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>Are you sure you want to delete <strong>"{subLeaderToDelete?.first_name} {subLeaderToDelete?.last_name}"</strong>?</Typography>
                    <Alert severity="warning" sx={{ bgcolor: '#fef3c7', border: '1px solid #fcd34d', '& .MuiAlert-icon': { color: '#d97706' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Warning: You will not be able to revert this action!</Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={handleDeleteCancel} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}
                        startIcon={<Delete />} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Assign Progress Banner */}
            {pollingSubLeaderId && bulkAssignStatusData && ['pending', 'processing'].includes(bulkAssignStatusData.status) && (
                <Card sx={{ mb: 2, mt: 2, borderRadius: 2, border: '1px solid', borderColor: 'primary.main' }}>
                    <CardContent sx={{ py: 2, px: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <People fontSize="small" color="primary" />
                            <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                Bulk Assignment in Progress
                            </Typography>
                            <Chip
                                label={bulkAssignStatusData.status === 'pending' ? 'Pending' : 'Processing'}
                                size="small"
                                color={bulkAssignStatusData.status === 'pending' ? 'warning' : 'info'}
                                sx={{ ml: 1 }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={bulkAssignStatusData.progress || 0}
                                    sx={{ height: 10, borderRadius: 5 }}
                                />
                            </Box>
                            <Typography variant="body2" fontWeight="bold" color="primary" sx={{ minWidth: 48 }}>
                                {Math.round(bulkAssignStatusData.progress || 0)}%
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {bulkAssignStatusData.assigned_citizens || 0} / {bulkAssignStatusData.total_citizens || '?'} citizens assigned
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {/* Bulk Assign Completed/Failed Banner */}
            {pollingSubLeaderId && bulkAssignStatusData && bulkAssignStatusData.status === 'completed' && (
                <Card sx={{ mb: 2, mt: 2, borderRadius: 2, border: '1px solid', borderColor: 'success.main' }}>
                    <CardContent sx={{ py: 2, px: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircle color="success" />
                            <Typography variant="subtitle2" fontWeight="bold" color="success.main">
                                Bulk Assignment Completed - {bulkAssignStatusData.assigned_citizens || 0} citizens assigned
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            )}
            {pollingSubLeaderId && bulkAssignStatusData && bulkAssignStatusData.status === 'failed' && (
                <Card sx={{ mb: 2, mt: 2, borderRadius: 2, border: '1px solid', borderColor: 'error.main' }}>
                    <CardContent sx={{ py: 2, px: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ErrorIcon color="error" />
                            <Typography variant="subtitle2" fontWeight="bold" color="error.main">
                                Bulk Assignment Failed: {bulkAssignStatusData.error || 'Unknown error'}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Bulk Assign Dialog */}
            <Dialog open={bulkAssignOpen} onClose={handleBulkAssignClose} maxWidth="lg" fullWidth fullScreen={window.innerWidth < 900}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupAdd color="primary" />
                        <Typography variant="h6">
                            Assign Citizens to {bulkAssignSubLeader?.first_name} {bulkAssignSubLeader?.last_name}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleBulkAssignClose} size="small">
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 2 }}>
                    {/* Location Filters */}
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Autocomplete
                                multiple
                                size="small"
                                options={constituenciesData || []}
                                getOptionLabel={(option: LocationOption) => option.name}
                                value={baConstituencies}
                                onChange={(_, newValue) => setBaConstituencies(newValue)}
                                loading={constituenciesLoading}
                                limitTags={1}
                                disableCloseOnSelect
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox size="small" style={{ marginRight: 8 }} checked={selected} />
                                        {option.name}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Constituencies"
                                        placeholder={baConstituencies.length === 0 ? "All" : ""}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <LocationCity fontSize="small" color="action" />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <VillageAutocomplete
                                multiple
                                value={baVillages}
                                onChange={(newValue: any) => setBaVillages(newValue)}
                                constituencyIds={baConstituencies.map(c => c.id)}
                                limitTags={1}
                                label="Villages"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <Autocomplete
                                multiple
                                size="small"
                                options={baBoothsData || []}
                                getOptionLabel={(option: LocationOption) => option.name}
                                value={baBooths}
                                onChange={(_, newValue) => setBaBooths(newValue)}
                                loading={baBoothsLoading}
                                limitTags={1}
                                disableCloseOnSelect
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox size="small" style={{ marginRight: 8 }} checked={selected} />
                                        {option.name}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Voting Booths"
                                        placeholder={baBooths.length === 0 ? "All" : ""}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <HowToVote fontSize="small" color="action" />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    {/* Additional Filters + Search */}
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search citizens..."
                                value={baSearch}
                                onChange={(e) => setBaSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Gender</InputLabel>
                                <Select value={baGender} label="Gender" onChange={(e) => setBaGender(e.target.value)}>
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="male">Male</MenuItem>
                                    <MenuItem value="female">Female</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 2 }}>
                            <Autocomplete
                                freeSolo
                                size="small"
                                options={communitiesOptions?.map((c: any) => c.name) || []}
                                value={baCommunity}
                                onChange={(_, newValue) => setBaCommunity(newValue || '')}
                                onInputChange={(_, newInputValue) => setBaCommunity(newInputValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Community" fullWidth />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 2 }}>
                            <TextField size="small" fullWidth label="Min Age" type="number" value={baMinAge}
                                onChange={(e) => setBaMinAge(e.target.value)} inputProps={{ min: 0, max: 150 }} />
                        </Grid>
                        <Grid size={{ xs: 6, sm: 2 }}>
                            <TextField size="small" fullWidth label="Max Age" type="number" value={baMaxAge}
                                onChange={(e) => setBaMaxAge(e.target.value)} inputProps={{ min: 0, max: 150 }} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 1 }}>
                            {(baConstituencies.length + baVillages.length + baBooths.length + (baGender ? 1 : 0) + (baMinAge ? 1 : 0) + (baMaxAge ? 1 : 0) + (baCommunity ? 1 : 0) + (baHasSubLeader ? 1 : 0)) > 0 && (
                                <BootstrapTooltip title="Reset Filters" placement="top">
                                    <IconButton size="small" onClick={() => {
                                        setBaConstituencies([]); setBaVillages([]); setBaBooths([]);
                                        setBaGender(''); setBaMinAge(''); setBaMaxAge('');
                                        setBaCommunity(''); setBaHasSubLeader(''); setBaSearch('');
                                    }}>
                                        <RestartAlt />
                                    </IconButton>
                                </BootstrapTooltip>
                            )}
                        </Grid>
                    </Grid>

                    {/* Select All Toggle + Selection Summary */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                                size="small"
                                variant={selectAllFiltered ? 'contained' : 'outlined'}
                                onClick={() => {
                                    setSelectAllFiltered(!selectAllFiltered);
                                    if (!selectAllFiltered) setSelectedCitizenIds([]);
                                }}
                            >
                                {selectAllFiltered ? `All ${totalFilteredCitizens.toLocaleString()} Selected` : 'Select All Matching Filters'}
                            </Button>
                            {selectAllFiltered && (
                                <Typography variant="caption" color="warning.main">
                                    Will use background job for large datasets
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <People fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight="bold" color="primary">
                                {assignCount.toLocaleString()} citizen{assignCount !== 1 ? 's' : ''} selected
                            </Typography>
                        </Box>
                    </Box>

                    {/* Citizens DataGrid */}
                    <DataGrid
                        rows={baCitizensData?.data?.data || []}
                        columns={citizenColumns}
                        loading={baCitizensLoading}
                        paginationModel={baPageModel}
                        onPaginationModelChange={setBaPageModel}
                        pageSizeOptions={[25, 50, 100]}
                        paginationMode="server"
                        rowCount={totalFilteredCitizens}
                        checkboxSelection={!selectAllFiltered}
                        rowSelectionModel={{ type: 'include', ids: new Set(selectAllFiltered ? [] : selectedCitizenIds) }}
                        onRowSelectionModelChange={(newSelection) => {
                            if (!selectAllFiltered) {
                                setSelectedCitizenIds(Array.from(newSelection.ids) as number[]);
                            }
                        }}
                        disableRowSelectionOnClick
                        autoHeight
                        density="compact"
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            '& .MuiDataGrid-row': { cursor: 'pointer' },
                            ...(selectAllFiltered && {
                                '& .MuiDataGrid-row': {
                                    bgcolor: 'action.selected',
                                    cursor: 'default',
                                },
                            }),
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                        {assignCount.toLocaleString()} citizen{assignCount !== 1 ? 's' : ''} will be assigned to {bulkAssignSubLeader?.first_name} {bulkAssignSubLeader?.last_name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" onClick={handleBulkAssignClose}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<GroupAdd />}
                            onClick={handleBulkAssignConfirm}
                            disabled={bulkAssignMutation.isPending || assignCount === 0}
                        >
                            {bulkAssignMutation.isPending ? 'Assigning...' : `Assign ${assignCount.toLocaleString()} Citizens`}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box >
    );
};

export default SubLeaders;
